import LoadingSpinner from 'components/LoadingSpinner';
import trackEvent from 'lib/analytics';
import { useEffect, type FC } from 'react';
import { DataStream } from 'services/DataStreamDefinitionService';
import { useTileEditorStepsContext } from 'ui/editor/dataStream/TileEditor/contexts/TileEditorStepsContext';
import { DataStreamVirtualList } from 'ui/editor/dataStream/TileEditor/dataStream/DataStreamVirtualList';
import { useObjectFilterObjects } from 'ui/editor/dataStream/TileEditor/hooks/objectFilters/useObjectFilterObjects';
import {
    useDataStreamFilters,
    type DataStreamFilterOption
} from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamFilters';
import { useScopeBaseQuery } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamObjectFilters';
import { useTileEditorStore } from 'ui/editor/dataStream/TileEditor/state/TileEditorStoreProvider';
import { useTileEditorDataStreams } from 'ui/editor/dataStream/TileEditor/state/useTileEditorDataStreams';
import { StepProgressButton } from 'ui/editor/dataStream/TileEditor/steps/StepProgressButton';
import { StepTitleAndControls } from 'ui/editor/dataStream/TileEditor/steps/StepTitleAndControls';
import { usePluginImportConfigEditorContext } from './PluginImportConfigEditorContext';
import { isLinkedToPluginConfig } from 'ui/editor/dataStream/TileEditor/steps/DataStreamConfiguration/SavedDataStreams';

export const PluginImportConfigDataStreamStep: FC<{
    dataStreamFilters: ReturnType<typeof useDataStreamFilters>;
    pluginId: string;
}> = ({ dataStreamFilters, pluginId }) => {
    const { canMoveToNextStep } = useTileEditorStepsContext();
    const { configId } = usePluginImportConfigEditorContext();

    const { dataStreamFilterOptions, isLoading } = dataStreamFilters;

    const { selectedDataStreamId, scope, dispatch } = useTileEditorStore((s) => ({
        selectedDataStreamId: s.dataStream.selectedDataStream?.id,
        filters: s.dataStream.filters,
        scope: s.scope,
        dispatch: s.dispatch
    }));

    const { data } = useTileEditorDataStreams();

    const { scopeBaseQuery, isLoading: isLoadingScopeBaseQuery } = useScopeBaseQuery(
        selectedDataStreamId
            ? ({
                  id: selectedDataStreamId
              } as DataStream)
            : undefined,
        undefined
    );
    const { objects } = useObjectFilterObjects({
        scopeBaseQuery,
        queryParams: {},
        isFilterQueryReady: !isLoadingScopeBaseQuery && selectedDataStreamId != null,
        options: { keepPreviousData: false }
    });

    useEffect(() => {
        if (scope.type !== 'objectList' && objects && objects.length > 0) {
            const nodeId = objects.find((obj) => obj.sourceId?.[0] === configId)?.id;
            if (nodeId) {
                dispatch({ type: 'setObjectListScope', selectedNodeIds: [nodeId] });
            }
        }
    }, [objects, configId, selectedDataStreamId, scope, dispatch]);

    if (isLoading) {
        return (
            <div className='flex items-center justify-center flex-1 w-full h-full'>
                <LoadingSpinner className='m-auto' />
            </div>
        );
    }

    const onSelectDataStream = (dataStream: DataStreamFilterOption) => {
        trackEvent('Data Stream Selected', {
            name: dataStream.displayName,
            plugin: dataStream.pluginName,
            configurable: Boolean(dataStream.template),
            configured: Boolean(dataStream.sourceTemplate)
        });

        if (dataStream.id !== selectedDataStreamId) {
            return dispatch({
                type: 'dataStream.selectDataStream',
                dataStream: data!.dataStreamsById.get(dataStream.id)!
            });
        }

        dispatch({ type: 'dataStream.clearSelectedDataStream' });
    };

    return (
        <div className='flex flex-col flex-1 h-full min-w-0 min-h-0 pt-4'>
            <div className='pb-4 pl-6 pr-5 border-b border-dividerTertiary'>
                <StepTitleAndControls title='Select a data stream' />
            </div>

            <div className='flex flex-1 min-h-0'>
                <div className='flex flex-col flex-1 min-w-0 min-h-0'>
                    <DataStreamVirtualList
                        dataStreams={
                            dataStreamFilterOptions?.filter((ds) => isLinkedToPluginConfig(pluginId, configId, ds)) ??
                            []
                        }
                        selectedDataStreamId={selectedDataStreamId}
                        onItemClick={onSelectDataStream}
                    />

                    <div className='flex self-end mt-5 mb-4 mr-5 space-x-4'>
                        <StepProgressButton
                            disabled={!selectedDataStreamId}
                            isLoading={Boolean(!canMoveToNextStep && selectedDataStreamId)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
